'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import type { Project } from '@/types/project';
import type {
  Concept,
  ConceptAngle,
  Control,
  Reference,
  Angle,
} from '@/types/concept';
import type { Render } from '@/types/render';

/**
 * Active tab in the middle panel
 */
export type MiddlePanelTab = 'design' | 'estimate' | 'album';

/**
 * Props for rendering the inline render settings panel
 */
export interface RenderSettingsPanelData {
  project: Project;
  concept: Concept;
  conceptAngles: ConceptAngle[];
  angles: Record<string, Angle>;
  renders: Record<string, Render[]>;
  controls: Record<string, Control[]>;
  references: Reference[];
  angleId?: string;
  currentRenderId?: string;
  onRefetch?: () => void;
}

/**
 * Snapshot of the concept being navigated away from, used to restore the angle
 * position when paging to an adjacent (prev/next) concept. See
 * `rememberAngleNav` on the context.
 */
export interface AngleNavMemory {
  conceptId: string;
  /** Ordered selection-values (undefined = main, then derivative ids by order). */
  order: Array<string | undefined>;
  prevId: string | null;
  nextId: string | null;
}

interface DesktopLayoutContextValue {
  renderSettingsData: RenderSettingsPanelData | null;
  setRenderSettingsData: (data: RenderSettingsPanelData | null) => void;
  isMiddlePanelVisible: boolean;
  /**
   * True when the page's content area is wide enough for the canvas AND the
   * inspector side by side (see `MIN_TWO_PANE_WIDTH` in ProjectDesktopLayout).
   * Decided by the width the page really has (window minus sidebar minus the
   * docked AI architect), not by the window width: a laptop with the sidebar
   * and the chat open gets the single-column layout and a big canvas.
   */
  isTwoPane: boolean;
  setIsTwoPane: (value: boolean) => void;
  // Middle panel state for syncing with main content
  activeTab: MiddlePanelTab;
  setActiveTab: (tab: MiddlePanelTab) => void;
  selectedAngleId: string | undefined;
  setSelectedAngleId: (angleId: string | undefined) => void;
  // Carry the selected angle's POSITION across concept navigation so paging
  // prev/next lands on the same view (users compare the same angle). This
  // context outlives ConceptEditor — which remounts per concept via
  // key={conceptId} — so it remembers the concept being left: its ordered angle
  // selection-values (undefined = main) and its prev/next neighbour ids. On the
  // next concept's mount ConceptEditor maps the old index onto the new angle
  // list, but ONLY when the new concept is that neighbour (= a real prev/next
  // page, not an arbitrary jump from a list — matches Apple/Android, which keep
  // the position only on the chevrons). Stored in a ref: pure bookkeeping, no
  // re-render.
  rememberAngleNav: (memory: AngleNavMemory) => void;
  getAngleNavMemory: () => AngleNavMemory | null;
  // Full-width topbar portal slot. The layout renders a stable DOM
  // `<div>` above both columns and exposes its node here via a ref
  // callback. Two-column screens (e.g. ConceptEditor) portal their
  // page header into it so the topbar stretches over content + aside.
  // Storing the DOM node (not JSX) keeps the React tree stable —
  // setTopBarSlot fires once on mount and once on unmount, so it
  // can't loop with downstream effects.
  topBarSlot: HTMLDivElement | null;
  setTopBarSlot: (node: HTMLDivElement | null) => void;
}

const DesktopLayoutContext = createContext<DesktopLayoutContextValue | null>(
  null
);

interface DesktopLayoutProviderProps {
  children: ReactNode;
}

/**
 * Content signature of a `Record<string, {id}[]>` map: the sorted set of child
 * ids across all buckets. Changes on add / delete / replace, stays stable
 * between renders when the underlying data is unchanged — so it is safe to
 * compare without risking an infinite publish→setState loop (unlike identity,
 * which can flip every render).
 */
function bucketedIdSignature(
  map: Record<string, Array<{ id: string }>>
): string {
  const ids: string[] = [];
  for (const bucket of Object.values(map)) {
    for (const item of bucket) ids.push(item.id);
  }
  return ids.sort().join(',');
}

/**
 * Check if render settings data has meaningfully changed.
 * Compares IDs plus content signatures of controls/renders (never object
 * identity) so the two-column inspector re-publishes when a sketch/render is
 * added, deleted, or replaced — while staying loop-safe.
 */
function hasDataChanged(
  prev: RenderSettingsPanelData | null,
  next: RenderSettingsPanelData | null
): boolean {
  if (prev === null && next === null) return false;
  if (prev === null || next === null) return true;

  // Compare by IDs and by content signatures (never object identity) to
  // prevent infinite loops. Without the controls/renders signatures a newly
  // created or deleted sketch never reached the two-column inspector, because
  // the concept/angle ids and conceptAngles length stay the same.
  return (
    prev.project.id !== next.project.id ||
    prev.concept.id !== next.concept.id ||
    prev.angleId !== next.angleId ||
    prev.currentRenderId !== next.currentRenderId ||
    prev.conceptAngles.length !== next.conceptAngles.length ||
    bucketedIdSignature(prev.controls) !== bucketedIdSignature(next.controls) ||
    bucketedIdSignature(prev.renders) !== bucketedIdSignature(next.renders)
  );
}

/**
 * Provider for desktop layout context.
 * Allows child components to provide data for the render settings panel.
 */
export function DesktopLayoutProvider({
  children,
}: DesktopLayoutProviderProps) {
  const [renderSettingsData, setRenderSettingsDataState] =
    useState<RenderSettingsPanelData | null>(null);
  const dataRef = useRef<RenderSettingsPanelData | null>(null);

  // Middle panel state
  const [activeTab, setActiveTab] = useState<MiddlePanelTab>('design');
  const [selectedAngleId, setSelectedAngleId] = useState<string | undefined>(
    undefined
  );

  const [isTwoPane, setIsTwoPane] = useState(false);

  // Topbar portal slot — DOM node only (no JSX in state). Setter is
  // stable so a `<div ref={setTopBarSlot}>` consumer fires the callback
  // exactly twice over a lifecycle (mount → node, unmount → null), with
  // no risk of an effect-driven loop.
  const [topBarSlot, setTopBarSlotState] = useState<HTMLDivElement | null>(
    null
  );
  const setTopBarSlot = useCallback((node: HTMLDivElement | null) => {
    setTopBarSlotState(node);
  }, []);

  // Remembered angle position for cross-concept navigation (see interface).
  const angleNavMemoryRef = useRef<AngleNavMemory | null>(null);
  const rememberAngleNav = useCallback((memory: AngleNavMemory) => {
    angleNavMemoryRef.current = memory;
  }, []);
  const getAngleNavMemory = useCallback(() => angleNavMemoryRef.current, []);

  const setRenderSettingsData = useCallback(
    (data: RenderSettingsPanelData | null) => {
      // Only update state if data has meaningfully changed
      if (hasDataChanged(dataRef.current, data)) {
        dataRef.current = data;
        setRenderSettingsDataState(data);
      }
    },
    []
  );

  const value = useMemo<DesktopLayoutContextValue>(
    () => ({
      renderSettingsData,
      setRenderSettingsData,
      isMiddlePanelVisible: renderSettingsData !== null,
      isTwoPane,
      setIsTwoPane,
      activeTab,
      setActiveTab,
      selectedAngleId,
      setSelectedAngleId,
      rememberAngleNav,
      getAngleNavMemory,
      topBarSlot,
      setTopBarSlot,
    }),
    [
      renderSettingsData,
      setRenderSettingsData,
      isTwoPane,
      activeTab,
      selectedAngleId,
      rememberAngleNav,
      getAngleNavMemory,
      topBarSlot,
      setTopBarSlot,
    ]
  );

  return (
    <DesktopLayoutContext.Provider value={value}>
      {children}
    </DesktopLayoutContext.Provider>
  );
}

/**
 * Hook to access the desktop layout context.
 */
export function useDesktopLayout() {
  const context = useContext(DesktopLayoutContext);
  if (!context) {
    throw new Error(
      'useDesktopLayout must be used within a DesktopLayoutProvider'
    );
  }
  return context;
}

/**
 * Hook to optionally access the desktop layout context.
 * Returns null if not within a provider.
 */
export function useOptionalDesktopLayout() {
  return useContext(DesktopLayoutContext);
}
